/*
 * consent.js — Consent seam (B2C-242 origin; wired to the real source by B2C-289)
 *
 * Pure seam: no DOM writes (one `document` listener is the only DOM touch),
 * no fetch, no third-party reference. Assigns a namespaced global
 * `window.CasinettoConsent`. Loaded with `defer`, LAYOUT-WIDE (decisions Q3)
 * so any consumer anywhere on the site reads one consent state. Tiny, so
 * there is no cost to loading it globally.
 *
 * Source: the Shopify Customer Privacy API (`window.Shopify.customerPrivacy`).
 * This file is the ONLY reader of that API in the theme — every other module
 * goes through the seam (docs/features/consent.md).
 *
 * The API arrives LATE. Shopify loads `consent-tracking-api.js` lazily from
 * its own bundle after DOMContentLoaded (measured 2026-09-25: the deferred
 * theme scripts execute at DCL ≈ 1098 ms, the API script is fetched at
 * ≈ 1117 ms), so a deferred consumer's first synchronous `has*()` read can be
 * `false` while consent is granted. The seam owns that case. At module init,
 * if the API is absent and `window.Shopify.loadFeatures` is a function, it
 * requests the feature `consent-tracking-api` EXACTLY ONCE (a guard flag is
 * set before the call, so no path can call it twice). When the callback
 * reports no error and the API is now present, the seam reads the state
 * through its own `has*()` methods and delivers it ONCE to every callback
 * registered at that moment — the one "ready" notification.
 *
 * Fail closed, in every path. When `window.Shopify`, `.customerPrivacy` or
 * the method is absent, every `has*` returns `false` and nothing throws. The
 * same when `loadFeatures` is not a function (no call, no notification), when
 * its callback reports an error, when the callback fires with the API still
 * absent, or when the callback never fires: no notification, no timer, no
 * poll, no retry. A missing API is an error state, not a "no rules, so
 * granted" state — recording while the consent system is down is the worst
 * state a consumer can be in.
 *
 * Change notifications: the platform emits `visitorConsentCollected` on
 * `document`. The seam subscribes ONCE and forwards a normalised object to
 * every registered callback. The event's key names differ from the method
 * names; the seam hides that:
 *   event.detail.analyticsAllowed → { analytics }
 *   event.detail.marketingAllowed → { marketing }
 *
 * The `onConsentChange` contract: you are notified of the first state the
 * platform can report, if the platform could not report one when you
 * registered, and of every change after. A consumer that registers after the
 * seam is ready gets no ready notification — `has*()` is its truth. So the
 * consumer pattern is: read `has*()` and subscribe in the SAME synchronous
 * run. No dedupe: a real change can fire inside the load window, so a
 * consumer may receive a change notification and then the ready notification;
 * consumers must be idempotent (hotjar.js keeps a `loaded` flag). The seam
 * stays dumb — it compares nothing and suppresses nothing.
 *
 * Consumers: assets/hotjar.js (analytics purpose). LoyaltyLion is mounted
 * site-wide by its app embed (Approach B) and rewards.js never called this
 * seam — it is not gated by the theme (debt/rewards-consent.md).
 *
 * Decision: decisions/b2c-289-hotjar.md §A and §A′. Reference:
 * docs/features/consent.md.
 */
(function () {
  'use strict';

  var callbacks = [];
  var listening = false;
  var ready = false; // the API was present at init, or arrived through loadFeatures
  var loadRequested = false; // loadFeatures is called at most once, ever

  // The one place the platform API is touched. Returns the API object or null.
  function api() {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      return window.Shopify.customerPrivacy;
    }
    return null;
  }

  // Reads a boolean method off the API; absent or throwing → false.
  function read(method) {
    var cp = api();
    if (!cp || typeof cp[method] !== 'function') return false;
    try {
      return Boolean(cp[method]());
    } catch (e) {
      return false;
    }
  }

  // () -> bool. Analytics purpose (session recording, heatmaps).
  function hasAnalyticsConsent() {
    return read('analyticsProcessingAllowed');
  }

  // () -> bool. Marketing purpose. Was a stub `true` until B2C-289; zero callers
  // at the time of the switch, so the change of meaning changed no behaviour.
  function hasMarketingConsent() {
    return read('marketingAllowed');
  }

  // Delivers one state to every callback registered right now, each in its own
  // try/catch so one bad consumer cannot break another.
  function deliver(state) {
    // Snapshot: a consumer that registers another consumer from inside its
    // callback does not widen this delivery.
    var list = callbacks.slice();
    for (var i = 0; i < list.length; i++) {
      try {
        list[i](state);
      } catch (e) {
        // Swallowed on purpose: a consumer's fault stays the consumer's.
      }
    }
  }

  // Forwards one platform event, normalised, to every callback.
  function handleConsentCollected(event) {
    var detail = event && event.detail;
    if (!detail || typeof detail !== 'object') return;
    deliver({
      analytics: Boolean(detail.analyticsAllowed),
      marketing: Boolean(detail.marketingAllowed)
    });
  }

  // loadFeatures callback. Error, or API still absent → stay closed: no
  // notification, no retry. Otherwise mark ready and deliver the first state
  // the platform can report, read through the seam's own fail-closed reads.
  // Delivered at most once even if the platform were to call back twice.
  function onFeaturesLoaded(error) {
    if (ready) return;
    if (error) return;
    if (!api()) return;
    ready = true;
    deliver({
      analytics: hasAnalyticsConsent(),
      marketing: hasMarketingConsent()
    });
  }

  // Runs once at module init (eager, not on first registration). API present →
  // ready, nothing to do. Absent → request it exactly once if the platform
  // loader exists; otherwise stay closed. No timer, no poll, no retry.
  function init() {
    if (api()) {
      ready = true;
      return;
    }
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    if (loadRequested) return;
    loadRequested = true;
    try {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        onFeaturesLoaded
      );
    } catch (e) {
      // Stay closed: the platform loader failed, so its consent system is down.
    }
  }

  // (fn) -> void. Registers `callback` for the first reportable state (only if
  // the API was absent at registration) and every change after. The document
  // listener is added on the first registration only.
  function onConsentChange(callback) {
    if (typeof callback !== 'function') return;
    callbacks.push(callback);
    if (listening) return;
    listening = true;
    document.addEventListener('visitorConsentCollected', handleConsentCollected);
  }

  window.CasinettoConsent = {
    hasAnalyticsConsent: hasAnalyticsConsent, // () -> bool (false if the API is absent)
    hasMarketingConsent: hasMarketingConsent, // () -> bool (false if the API is absent)
    onConsentChange: onConsentChange // (fn) -> void; cb({ analytics, marketing }) on ready-if-late, then on change
  };

  init();
})();
